import React, { useState, useEffect } from 'react';
import {
    Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton,
    ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar, Menu, MenuItem,
    Chip, Tooltip, Button, useMediaQuery, useTheme, Collapse, Alert, AlertTitle
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BusinessIcon from '@mui/icons-material/Business';
import DomainIcon from '@mui/icons-material/Domain';
import EmailIcon from '@mui/icons-material/Email';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LogoutIcon from '@mui/icons-material/Logout';
import MailIcon from '@mui/icons-material/Mail';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import StorageIcon from '@mui/icons-material/Storage';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SecurityIcon from '@mui/icons-material/Security';
import BuildIcon from '@mui/icons-material/Build';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageIcon from '@mui/icons-material/Language';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import HomeIcon from '@mui/icons-material/Home';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LionIcon from './LionIcon';
import { useBranding } from '../context/BrandingContext';
import TrialUpgradeListener from './TrialUpgradeListener';
import CampaignIcon from '@mui/icons-material/Campaign';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import MobileBottomNav from './MobileBottomNav';
import ActivationChecklist from './ActivationChecklist';
import PoweredByBadge from './PoweredByBadge';

const DRAWER_FULL = 240;
const DRAWER_MINI = 64;

const Layout = () => {
    const { user, logout, impersonating, stopImpersonating } = useAuth();
    const { mode, toggleTheme } = useThemeMode();
    const { branding, loadUserBranding, resetBranding } = useBranding();
    const { lang, setLang, t, isRtl, languages } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [mobileOpen, setMobileOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [langAnchorEl, setLangAnchorEl] = useState(null);
    const [collapsed, setCollapsed] = useState(() => {
        try { return localStorage.getItem('emailLion_sidebarCollapsed') === 'true'; } catch { return false; }
    });

    // Load org branding when user is authenticated
    useEffect(() => {
        if (user && !branding?.whitelabel) {
            loadUserBranding();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    // Load org modules (which addon modules are enabled for this org)
    const [orgModules, setOrgModules] = useState([]);
    const [needsMailboxConnect, setNeedsMailboxConnect] = useState(false);
    const [dismissConnectBanner, setDismissConnectBanner] = useState(() => {
        try { return sessionStorage.getItem('emailLion_dismissConnectBanner') === '1'; } catch { return false; }
    });
    useEffect(() => {
        if (user?.role === 'super_admin') {
            setOrgModules(['proposals', 'automations', 'filing', 'ai_agent']); // super_admin has all modules
            return;
        }
        const token = localStorage.getItem('token');
        if (!token || !user) return;
        fetch('/api/admin/modules', { headers: { 'Authorization': `Bearer ${token}` } })
            .then(r => r.ok ? r.json() : { modules: [] })
            .then(data => setOrgModules(data.modules || []))
            .catch(() => setOrgModules([]));
    }, [user]);

    // Soft nudge: user has no connected IMAP mailbox yet
    useEffect(() => {
        if (!user || impersonating) {
            setNeedsMailboxConnect(false);
            return;
        }
        // Skip on the connect page itself to avoid double messaging
        if (location.pathname.startsWith('/webmail')) {
            setNeedsMailboxConnect(false);
            return;
        }
        let cancelled = false;
        const token = localStorage.getItem('token');
        if (!token) return;
        fetch('/api/mail/accounts', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => (r.ok ? r.json() : { accounts: [] }))
            .then((data) => {
                if (cancelled) return;
                const list = data.accounts || data || [];
                setNeedsMailboxConnect(Array.isArray(list) ? list.length === 0 : false);
            })
            .catch(() => { if (!cancelled) setNeedsMailboxConnect(false); });
        return () => { cancelled = true; };
    }, [user, impersonating, location.pathname]);

    const toggleCollapsed = () => {
        const next = !collapsed;
        setCollapsed(next);
        localStorage.setItem('emailLion_sidebarCollapsed', String(next));
    };

    const currentDrawerWidth = collapsed ? DRAWER_MINI : DRAWER_FULL;

    // Top-level items (always visible, no grouping)
    const topItems = [
        { text: t('layout.dashboard'), icon: <DashboardIcon />, path: '/dashboard', roles: ['super_admin', 'multi_org_admin', 'org_admin'] },
        { text: t('layout.webmail'), icon: <MailIcon />, path: '/webmail', roles: ['user', 'super_admin', 'org_admin', 'multi_org_admin'] },
        { text: t('layout.calendar'), icon: <CalendarMonthIcon />, path: '/calendar', roles: ['user', 'super_admin', 'org_admin', 'multi_org_admin'] },
    ];

    // Grouped menu sections
    const menuGroups = [
        {
            id: 'management',
            label: t('layout.orgManagement'),
            icon: <AdminPanelSettingsIcon />,
            roles: ['super_admin', 'multi_org_admin', 'org_admin'],
            items: [
                { text: t('layout.organizations'), icon: <BusinessIcon />, path: '/organizations', roles: ['super_admin', 'multi_org_admin'] },
                { text: t('layout.domains'), icon: <DomainIcon />, path: '/domains', roles: ['super_admin', 'multi_org_admin', 'org_admin'] },
                { text: t('layout.mailboxes'), icon: <EmailIcon />, path: '/mailboxes', roles: ['super_admin', 'multi_org_admin', 'org_admin'] },
                { text: t('layout.users'), icon: <PeopleIcon />, path: '/users', roles: ['super_admin', 'multi_org_admin', 'org_admin'] },
            ],
        },
        {
            id: 'billing',
            label: t('layout.operations'),
            icon: <AccountBalanceIcon />,
            roles: ['super_admin', 'multi_org_admin', 'org_admin'],
            items: [
                { text: t('layout.plans'), icon: <StorageIcon />, path: '/plans', roles: ['super_admin', 'org_admin'] },
                { text: t('layout.proposals'), icon: <ReceiptLongIcon />, path: '/proposals', roles: ['super_admin', 'multi_org_admin', 'org_admin'], module: 'proposals' },
                { text: t('layout.billing'), icon: <ReceiptLongIcon />, path: '/billing', roles: ['super_admin', 'org_admin'] },
                { text: t('layout.branding'), icon: <BrandingWatermarkIcon />, path: '/branding', roles: ['super_admin', 'multi_org_admin', 'org_admin'] },
            ],
        },
        {
            id: 'monitoring',
            label: t('layout.monitoring'),
            icon: <SecurityIcon />,
            roles: ['super_admin', 'multi_org_admin', 'org_admin'],
            items: [
                { text: t('layout.reports'), icon: <AssessmentIcon />, path: '/logs', roles: ['super_admin', 'multi_org_admin'] },
                { text: t('layout.systemHealth'), icon: <MonitorHeartIcon />, path: '/system-health', roles: ['super_admin'] },
                { text: t('layout.auditLog'), icon: <GavelIcon />, path: '/audit-log', roles: ['super_admin', 'org_admin'] },
            ],
        },
        {
            id: 'tools',
            label: t('layout.tools'),
            icon: <BuildIcon />,
            roles: ['super_admin', 'org_admin', 'user'],
            items: [
                { text: t('layout.automations'), icon: <AutoFixHighIcon />, path: '/automations', roles: ['super_admin', 'multi_org_admin', 'org_admin', 'user'], module: 'automations' },
                { text: t('layout.filing'), icon: <FolderOpenIcon />, path: '/filing', roles: ['super_admin', 'multi_org_admin', 'org_admin', 'user'], module: 'filing' },
                { text: t('layout.aiAgent'), icon: <SmartToyIcon />, path: '/ai-agent', roles: ['super_admin', 'multi_org_admin', 'org_admin', 'user'], module: 'ai_agent' },
                { text: t('layout.smartCleanup'), icon: <CleaningServicesIcon />, path: '/smart-cleanup', roles: ['super_admin', 'multi_org_admin', 'org_admin', 'user'] },
                { text: t('layout.autoReplyLog'), icon: <SmartToyIcon />, path: '/auto-reply-logs', roles: ['super_admin', 'org_admin', 'user'] },
                { text: t('layout.migration'), icon: <SyncAltIcon />, path: '/migration', roles: ['super_admin', 'org_admin', 'user'] },
                { text: t('layout.marketingCockpit'), icon: <CampaignIcon />, path: '/marketing', roles: ['super_admin'] },
                { text: t('layout.salesEngine'), icon: <TrendingUpIcon />, path: '/sales', roles: ['super_admin'] },
                { text: t('layout.integrations'), icon: <IntegrationInstructionsIcon />, path: '/integrations', roles: ['super_admin', 'org_admin', 'multi_org_admin'] },
                { text: t('layout.settings'), icon: <SettingsIcon />, path: '/settings', roles: ['super_admin', 'org_admin', 'user'] },
            ],
        },
    ];

    // Exclusive accordion: at most one sidebar section is open.
    const persistOpenGroup = (next) => {
        try { localStorage.setItem('emailLion_openMenuGroups', JSON.stringify(next)); } catch { /* ignore */ }
        return next;
    };

    const [openGroups, setOpenGroups] = useState(() => {
        const matching = menuGroups.find(g => g.items.some(i => location.pathname === i.path));
        try {
            const saved = JSON.parse(localStorage.getItem('emailLion_openMenuGroups') || 'null');
            if (saved && typeof saved === 'object') {
                const openIds = Object.keys(saved).filter((k) => saved[k]);
                if (matching && openIds.includes(matching.id)) return persistOpenGroup({ [matching.id]: true });
                if (openIds.length) return persistOpenGroup({ [openIds[0]]: true });
            }
        } catch { /* ignore */ }
        return matching ? persistOpenGroup({ [matching.id]: true }) : {};
    });

    const toggleGroup = (groupId) => {
        setOpenGroups((prev) => persistOpenGroup(prev[groupId] ? {} : { [groupId]: true }));
    };

    const visibleTopItems = topItems.filter(item => item.roles.includes(user?.role));
    const isRegularUser = user?.role === 'user';
    const isWebmail = location.pathname === '/webmail';

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Logo area */}
            <Toolbar sx={{ justifyContent: 'center', flexDirection: 'column', py: 1.5, minHeight: 64 }}>
                {collapsed ? (
                    <Tooltip title={branding?.whitelabel ? branding.companyName : 'Email Lion'} placement="left">
                        <Box sx={{ cursor: 'pointer' }}>
                            {branding?.whitelabel && branding.logoUrl ? (
                                <img src={branding.logoUrl} alt="" style={{ height: 32, maxWidth: 40, objectFit: 'contain' }} />
                            ) : (
                                <LionIcon sx={{ fontSize: 32 }} />
                            )}
                        </Box>
                    </Tooltip>
                ) : (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {branding?.whitelabel && branding.logoUrl ? (
                                <img src={branding.logoUrl} alt={branding.companyName} style={{ height: 36, maxWidth: 160, objectFit: 'contain' }} />
                            ) : (
                                <>
                                    <LionIcon sx={{ fontSize: 36 }} />
                                    <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                                        Email Lion
                                    </Typography>
                                </>
                            )}
                        </Box>
                        {isRegularUser && !branding?.whitelabel && (
                            <Typography variant="caption" color="text.secondary">WebMail</Typography>
                        )}
                    </>
                )}
            </Toolbar>
            <Divider />

            {/* Impersonation banner */}
            {impersonating && !collapsed && (
                <Box sx={{ p: 1, bgcolor: 'warning.light', textAlign: 'center' }}>
                    <Typography variant="caption" fontWeight="bold">{t('layout.viewingAsUser')}</Typography>
                    <Button size="small" onClick={() => { stopImpersonating(); navigate('/dashboard'); }} startIcon={<SwapHorizIcon />} sx={{ mt: 0.5, display: 'block', mx: 'auto' }}>
                        {t('layout.backToAdmin')}
                    </Button>
                </Box>
            )}
            {impersonating && collapsed && (
                <Tooltip title={t('layout.backToAdmin')} placement="left">
                    <IconButton
                        color="warning"
                        onClick={() => { stopImpersonating(); navigate('/dashboard'); }}
                        sx={{ mx: 'auto', mt: 0.5 }}
                    >
                        <SwapHorizIcon />
                    </IconButton>
                </Tooltip>
            )}

            {/* Menu items */}
            <List sx={{ flex: 1, overflow: 'auto', pt: 0.5 }}>
                {/* Top-level items (Dashboard, Webmail) */}
                {visibleTopItems.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                        <Tooltip title={collapsed ? item.text : ''} placement="left" arrow>
                            <ListItemButton
                                selected={location.pathname === item.path}
                                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                                sx={{
                                    minHeight: 44,
                                    justifyContent: collapsed ? 'center' : 'initial',
                                    px: collapsed ? 1.5 : 2.5,
                                }}
                            >
                                <ListItemIcon sx={{
                                    color: location.pathname === item.path ? 'primary.main' : 'inherit',
                                    minWidth: collapsed ? 0 : 40,
                                    justifyContent: 'center',
                                }}>
                                    {item.icon}
                                </ListItemIcon>
                                {!collapsed && (
                                    <ListItemText
                                        primary={item.text}
                                        primaryTypographyProps={{ fontSize: '0.875rem' }}
                                    />
                                )}
                            </ListItemButton>
                        </Tooltip>
                    </ListItem>
                ))}

                {visibleTopItems.length > 0 && <Divider sx={{ my: 0.5 }} />}

                {/* Grouped sections */}
                {menuGroups.map((group) => {
                    // Filter items in this group by role and enabled organization modules
                    const visibleGroupItems = group.items.filter(i => {
                        if (!i.roles.includes(user?.role)) return false;
                        if (i.module && user?.role !== 'super_admin' && !orgModules.includes(i.module)) return false;
                        return true;
                    });
                    // Don't render the group if no items are visible or group roles don't match
                    if (visibleGroupItems.length === 0 || !group.roles.includes(user?.role)) return null;

                    const isGroupOpen = openGroups[group.id] || false;
                    const hasActiveChild = visibleGroupItems.some(i => location.pathname === i.path);

                    return (
                        <React.Fragment key={group.id}>
                            {/* Group header */}
                            <ListItem disablePadding sx={{ display: 'block' }}>
                                <Tooltip title={collapsed ? group.label : ''} placement="left" arrow>
                                    <ListItemButton
                                        onClick={() => {
                                            if (collapsed) {
                                                // When collapsed, expand sidebar first then open the group
                                                setCollapsed(false);
                                                localStorage.setItem('emailLion_sidebarCollapsed', 'false');
                                                setOpenGroups(persistOpenGroup({ [group.id]: true }));
                                            } else {
                                                toggleGroup(group.id);
                                            }
                                        }}
                                        sx={{
                                            minHeight: 40,
                                            justifyContent: collapsed ? 'center' : 'initial',
                                            px: collapsed ? 1.5 : 2.5,
                                            bgcolor: hasActiveChild ? 'action.selected' : 'transparent',
                                            borderRadius: 0,
                                        }}
                                    >
                                        <ListItemIcon sx={{
                                            color: hasActiveChild ? 'primary.main' : 'text.secondary',
                                            minWidth: collapsed ? 0 : 40,
                                            justifyContent: 'center',
                                        }}>
                                            {group.icon}
                                        </ListItemIcon>
                                        {!collapsed && (
                                            <>
                                                <ListItemText
                                                    primary={group.label}
                                                    primaryTypographyProps={{
                                                        fontSize: '0.8rem',
                                                        fontWeight: hasActiveChild ? 'bold' : 'medium',
                                                        color: hasActiveChild ? 'primary.main' : 'text.secondary',
                                                    }}
                                                />
                                                {isGroupOpen ? <ExpandLessIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                                            </>
                                        )}
                                    </ListItemButton>
                                </Tooltip>
                            </ListItem>

                            {/* Group children */}
                            {!collapsed && (
                                <Collapse in={isGroupOpen} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {visibleGroupItems.map((item) => (
                                            <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
                                                <ListItemButton
                                                    selected={location.pathname === item.path}
                                                    onClick={() => { navigate(item.path); setMobileOpen(false); }}
                                                    sx={{
                                                        minHeight: 38,
                                                        pr: 4,
                                                        pl: 2,
                                                    }}
                                                >
                                                    <ListItemIcon sx={{
                                                        color: location.pathname === item.path ? 'primary.main' : 'inherit',
                                                        minWidth: 32,
                                                        justifyContent: 'center',
                                                    }}>
                                                        {item.icon}
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={item.text}
                                                        primaryTypographyProps={{ fontSize: '0.82rem' }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            )}
                        </React.Fragment>
                    );
                })}
            </List>

            {/* Collapse/Expand toggle at bottom */}
            <Divider />
            <Box sx={{ p: 0.5, display: 'flex', justifyContent: 'center' }}>
                <Tooltip title={collapsed ? t('layout.expandMenu') : t('layout.collapseMenu')} placement="left">
                    <IconButton onClick={toggleCollapsed} size="small" sx={{ color: 'text.secondary' }}>
                        {collapsed ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', '@supports (height: 100svh)': { minHeight: '100svh' }, height: 'auto', overflow: 'visible' }}>
            {/* AppBar */}
            <AppBar
                position="fixed"
                sx={{
                    width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
                    ml: { sm: `${currentDrawerWidth}px` },
                    transition: 'width 0.2s ease, margin 0.2s ease',
                }}
                elevation={isMobile ? 0 : 4}
            >
                <Toolbar sx={{ minHeight: { xs: 48, sm: 64 }, px: { xs: 1, sm: 2 } }}>
                    {/* Mobile: hamburger menu */}
                    <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ display: { sm: 'none' }, mr: 0.5 }}>
                        <MenuIcon />
                    </IconButton>

                    {/* Mobile: Logo */}
                    {isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LionIcon sx={{ fontSize: 24 }} />
                            <Typography variant="subtitle2" fontWeight="bold" color="inherit">
                                Email Lion
                            </Typography>
                        </Box>
                    )}

                    {impersonating && !isMobile && (
                        <Chip
                            icon={<SwapHorizIcon />}
                            label={t('layout.viewingAsUserClick')}
                            color="warning"
                            onClick={() => { stopImpersonating(); navigate('/dashboard'); }}
                            sx={{ mr: 2 }}
                        />
                    )}

                    {/* Quick links */}
                    {!isMobile && (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title={t('layout.backToWebsite')}>
                                <Button
                                    color="inherit"
                                    size="small"
                                    startIcon={<HomeIcon fontSize="small" />}
                                    onClick={() => window.open('/', '_blank')}
                                    sx={{ fontSize: '0.8rem', textTransform: 'none', opacity: 0.85, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.1)' } }}
                                >
                                    {t('layout.website')}
                                </Button>
                            </Tooltip>
                            <Tooltip title={t('layout.helpTooltip')}>
                                <Button
                                    color="inherit"
                                    size="small"
                                    startIcon={<HelpOutlineIcon fontSize="small" />}
                                    onClick={() => window.open('/help', '_blank')}
                                    sx={{ fontSize: '0.8rem', textTransform: 'none', opacity: 0.85, '&:hover': { opacity: 1, bgcolor: 'rgba(255,255,255,0.1)' } }}
                                >
                                    {t('layout.help')}
                                </Button>
                            </Tooltip>
                        </Box>
                    )}

                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
                        <Tooltip title={t('layout.language')}>
                            <IconButton color="inherit" onClick={(e) => setLangAnchorEl(e.currentTarget)} size="small">
                                <LanguageIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Menu anchorEl={langAnchorEl} open={Boolean(langAnchorEl)} onClose={() => setLangAnchorEl(null)}>
                            {languages.map(l => (
                                <MenuItem
                                    key={l.code}
                                    selected={l.code === lang}
                                    onClick={() => { setLang(l.code); setLangAnchorEl(null); }}
                                >
                                    {l.nativeName}
                                </MenuItem>
                            ))}
                        </Menu>
                        <Tooltip title={mode === 'light' ? t('layout.darkMode') : t('layout.lightMode')}>
                            <IconButton color="inherit" onClick={toggleTheme} size="small">
                                {mode === 'light' ? <DarkModeIcon fontSize="small" /> : <LightModeIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        {/* Hide role chip on mobile */}
                        {!isMobile && (
                            <Chip
                                label={t('layout.roles.' + user?.role) || user?.role}
                                size="small"
                                variant="outlined"
                                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
                            />
                        )}
                        {!isMobile && <Typography variant="subtitle2">{user?.fullName}</Typography>}
                        <IconButton size={isMobile ? 'small' : 'large'} onClick={(e) => setAnchorEl(e.currentTarget)} color="inherit">
                            <Avatar sx={{ width: isMobile ? 28 : 32, height: isMobile ? 28 : 32, bgcolor: 'rgba(255,255,255,0.2)', fontSize: isMobile ? '0.8rem' : '1rem' }}>
                                {user?.fullName?.charAt(0)?.toUpperCase() || 'U'}
                            </Avatar>
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                            <MenuItem disabled>
                                <Box>
                                    <Typography variant="body2" fontWeight="bold">{user?.fullName}</Typography>
                                    <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
                                </Box>
                            </MenuItem>
                            <Divider />
                            {isMobile && (
                                <MenuItem disabled>
                                    <Chip label={t('layout.roles.' + user?.role) || user?.role} size="small" color="primary" />
                                </MenuItem>
                            )}
                            <MenuItem onClick={() => { navigate('/settings'); setAnchorEl(null); }}>
                                <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                                {t('layout.settings')}
                            </MenuItem>
                            <MenuItem onClick={() => { window.open('/help', '_blank'); setAnchorEl(null); }}>
                                <ListItemIcon><HelpOutlineIcon fontSize="small" /></ListItemIcon>
                                {t('layout.helpCenter')}
                            </MenuItem>
                            <MenuItem onClick={() => { window.open('/', '_blank'); setAnchorEl(null); }}>
                                <ListItemIcon><HomeIcon fontSize="small" /></ListItemIcon>
                                {t('layout.backToWebsite')}
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={() => { resetBranding(); logout(); navigate('/login'); }}>
                                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                                {t('layout.logout')}
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Sidebar Navigation */}
            <Box component="nav" sx={{ width: { sm: currentDrawerWidth }, flexShrink: { sm: 0 }, transition: 'width 0.2s ease' }}>
                {/* Mobile drawer - slide from right */}
                <Drawer variant="temporary" anchor={isRtl ? 'right' : 'left'} open={mobileOpen} onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true, BackdropProps: { onClick: () => setMobileOpen(false) } }}
                    sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { width: Math.min(DRAWER_FULL, 280), maxWidth: '80vw' } }}>
                    {drawerContent}
                </Drawer>
                {/* Desktop drawer - collapsible */}
                <Drawer variant="permanent" anchor={isRtl ? 'right' : 'left'}
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        '& .MuiDrawer-paper': {
                            width: currentDrawerWidth,
                            left: 0,
                            right: 'auto',
                            transition: 'width 0.2s ease',
                            overflowX: 'hidden',
                        },
                    }}
                    open>
                    {drawerContent}
                </Drawer>
            </Box>

            {/* Main Content */}
            <Box component="main" sx={{
                flexGrow: 1,
                p: isMobile ? (isWebmail ? 0 : 1) : (isRegularUser && isWebmail ? 1 : 3),
                width: { xs: '100%', sm: `calc(100% - ${currentDrawerWidth}px)` },
                transition: 'width 0.2s ease',
                pb: { xs: '72px', sm: 0 }, // Bottom padding for mobile nav
                minHeight: '100vh',
                '@supports (height: 100svh)': { minHeight: '100svh' },
                overflow: 'visible',
            }}>
                <Toolbar sx={{ minHeight: { xs: 48, sm: 64 } }} />
                {needsMailboxConnect && !dismissConnectBanner && (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2, alignItems: 'center' }}
                        action={
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Button
                                    color="inherit"
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        try { sessionStorage.setItem('emailLion_dismissConnectBanner', '1'); } catch { /* ignore */ }
                                        setDismissConnectBanner(true);
                                    }}
                                >
                                    {t('inbox.connectBannerDismiss')}
                                </Button>
                                <Button
                                    color="inherit"
                                    size="small"
                                    variant="contained"
                                    onClick={() => navigate('/webmail?connect=1')}
                                >
                                    {t('inbox.connectBannerCta')}
                                </Button>
                            </Box>
                        }
                    >
                        <AlertTitle>{t('inbox.connectBannerTitle')}</AlertTitle>
                        {t('inbox.connectBannerBody')}
                    </Alert>
                )}
                <Outlet />
            </Box>

            <PoweredByBadge sx={{
                position: 'fixed',
                bottom: { xs: 56, sm: 0 },
                left: 0,
                right: 0,
                bgcolor: 'background.paper',
                borderTop: '1px solid',
                borderColor: 'divider',
                zIndex: 1100,
            }} />

            {/* Mobile Bottom Navigation */}
            <MobileBottomNav />

            {/* Activation checklist: connect → send → AI → automation */}
            <ActivationChecklist />

            {/* Listens for 402 upgrade-required events from the axios interceptor */}
            <TrialUpgradeListener />
        </Box>
    );
};

export default Layout;
